import { createContext, useContext, useEffect, type ReactNode } from "react";
import { action, computed, makeAutoObservable, observable } from "mobx";
import { useLocalObservable, observer } from "mobx-react-lite";
import { Outlet } from "react-router-dom";

import { useGetProviderApiKeyStatus } from "@/queries";

export class ApiKeyStore {
  @observable private _apiKeys = new Map<string, boolean>();
  @observable private _apiKeyConfigs = new Map<string, string>();

  constructor() {
    makeAutoObservable(this);
    this._apiKeys = new Map();
    this._apiKeyConfigs = new Map();
  }

  @action
  setApiKeys(apiKeys: Record<string, boolean | string | null>) {
    const apiKeysMap = new Map<string, boolean>();
    const configsMap = new Map<string, string>();
    for (const [key, value] of Object.entries(apiKeys)) {
      if (typeof value === "boolean") {
        apiKeysMap.set(key, value);
      } else if (typeof value === "string") {
        configsMap.set(key, value);
      }
    }
    this._apiKeys = apiKeysMap;
    this._apiKeyConfigs = configsMap;
  }

  @action
  removeApiKey(provider: string) {
    this?._apiKeys?.delete(provider);
  }

  @action
  getApiKey(provider: string) {
    return this?._apiKeys?.get(provider);
  }

  @action
  setApiKey(provider: string, isSet: boolean) {
    this?._apiKeys?.set(provider, isSet);
  }

  @action
  setApiKeyConfig(key: string, value: string) {
    this?._apiKeyConfigs?.set(key, value);
  }

  @action
  getApiKeyConfig(key: string): string {
    return this?._apiKeyConfigs?.get(key) ?? "";
  }

  @action
  getisApiKeySet(provider: string) {
    return this?._apiKeys?.has(provider);
  }

  @computed
  get isLoading() {
    return this?._apiKeys?.size === 0;
  }
}

const ApiKeyStoreContext = createContext<ApiKeyStore | null>(null);

export const ApiKeyStoreScope = observer(({ children }: { children: ReactNode }) => {
  const { data: apiKeys } = useGetProviderApiKeyStatus();
  const store = useLocalObservable(() => new ApiKeyStore());

  useEffect(() => {
    if (apiKeys) {
      store.setApiKeys(apiKeys);
    }
  }, [apiKeys, store]);

  return <ApiKeyStoreContext.Provider value={store}>{children}</ApiKeyStoreContext.Provider>;
});

export const ApiKeyStoreProvider = observer(() => (
  <ApiKeyStoreScope>
    <Outlet />
  </ApiKeyStoreScope>
));

export const useApiKeyStore = () => {
  const store = useContext(ApiKeyStoreContext);
  if (!store) {
    throw new Error("useApiKeyStore must be used within a ApiKeyStoreProvider");
  }
  return store;
};
